import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid } from 'date-fns';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { RequiredChip } from '@/components/Chips/RequiredChip';
import { coerceDate } from '@/utils/coerceDate';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { formTextFieldStyles as sx } from './FormTextField.styles';

const DATE_STORAGE_FORMAT = 'yyyy-MM-dd';

type Props = {
	label: string;
	placeholder?: string;
	isRequired?: boolean;
	isDirty: boolean;
	indicateError: boolean;
	errorMessage?: string;
	defaultValue?: string;
	onChange?: (value: string) => void;
	disabled?: boolean;
	name?: string;
	id?: string;
	isDateField?: boolean;
	maxLength?: number;
} & RequirePair<'canAddNewItem', 'onAddNewItemClick', () => void> &
	RequirePair<'canBeRemoved', 'onRemove', () => void>;

export const FormTextField = ({
	label,
	placeholder = label,
	isRequired,
	isDirty,
	indicateError,
	errorMessage,
	canBeRemoved,
	defaultValue,
	onRemove,
	onChange,
	disabled,
	name,
	id,
	isDateField,
	maxLength,
	canAddNewItem,
	onAddNewItemClick
}: Props) => {
	return (
		<Box sx={() => sx.root(!!canAddNewItem)}>
			<Typography component='label' htmlFor={id} sx={sx.label}>
				{label}
			</Typography>

			<Box>
				<Box sx={sx.fieldWrap}>
					<Box sx={sx.field}>
						{isDateField ? (
							<DatePicker
								defaultValue={defaultValue ? coerceDate(defaultValue) : null}
								format={DATE_STORAGE_FORMAT}
								disabled={disabled}
								onChange={newValue =>
									onChange?.(
										newValue && isValid(newValue)
											? format(newValue, DATE_STORAGE_FORMAT)
											: ''
									)
								}
								slotProps={{
									textField: {
										id,
										name,
										helperText: errorMessage,
										error: !!errorMessage || indicateError,
										variant: 'outlined',
										size: 'small',
										fullWidth: true,
										sx: sx.dateField(!!indicateError, !!isDirty),
										slotProps: {
											htmlInput: { 'aria-required': !!isRequired }
										}
									}
								}}
							/>
						) : (
							<TextField
								id={id}
								name={name}
								placeholder={placeholder}
								defaultValue={defaultValue}
								helperText={errorMessage}
								error={!!errorMessage || indicateError}
								variant='outlined'
								type='text'
								size='small'
								fullWidth
								disabled={disabled}
								onChange={e => onChange?.(e.target.value)}
								sx={sx.textField(!!indicateError, !!isDirty)}
								slotProps={{
									htmlInput: {
										'aria-required': !!isRequired,
										maxLength
									}
								}}
							/>
						)}

						<Box sx={sx.badgeWrap}>
							{isRequired ? (
								<RequiredChip indicateError={indicateError} />
							) : null}
						</Box>
					</Box>

					{canBeRemoved && onRemove && (
						<RemoveFormFieldButton
							onClick={onRemove}
							customSx={sx.removeButton}
						/>
					)}
				</Box>

				{canAddNewItem && (
					<Box sx={sx.addNewItemButtonWrap}>
						<AddFormFieldButton onClick={onAddNewItemClick} />
					</Box>
				)}
			</Box>
		</Box>
	);
};
